import { showNotification } from "@mantine/notifications"
import { IconDownload, IconTrash, IconUpload } from "@tabler/icons-react"
import { ButtonWithMenu, ButtonWithMenuProps } from "@tfg/ui"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  generateExportByKeyRequestDtoKeyEnum,
  useGenerateExportByKey,
} from "~/gen/api"

export type CatalogActionsMenuProps = {
  selectedProductIds: string[]
  onProductsDeleteClick: () => void
  isProductsDeleteLoading: boolean
}

export const CatalogActionsMenu = ({
  selectedProductIds,
  onProductsDeleteClick,
  isProductsDeleteLoading,
}: CatalogActionsMenuProps) => {
  const { t } = useTranslation(["common", "products", "dataTransfer"])

  const { mutate: generateExportByKey } = useGenerateExportByKey()

  const onCatalogExportClick = useCallback(() => {
    window.open("/data-transfer/export", "_blank")

    generateExportByKey(
      {
        data: {
          key: generateExportByKeyRequestDtoKeyEnum.EXPORT_CATALOG,
        },
      },
      {
        onSuccess() {
          showNotification({
            color: "green",
            message: t("dataTransfer:export.notifications.generate.success"),
          })

          queryClient.invalidateQueries({
            queryKey: [{ url: "/v1/export/export-requests" }],
          })
        },
      }
    )
  }, [generateExportByKey, t])

  const items = useMemo<ButtonWithMenuProps["items"]>(() => {
    const result = [
      {
        icon: <IconDownload />,
        label: `${t("common:actionButtons.export")}`,
        onClick: onCatalogExportClick,
      },
      {
        icon: <IconTrash />,
        label: `${t("products:bulkActions.delete.action.name", { count: selectedProductIds.length })}`,
        onClick: onProductsDeleteClick,
        disabled: selectedProductIds.length === 0,
      },
      {
        icon: <IconUpload />,
        label: `${t("products:bulkActions.import.actionName")}`,
        onClick: () => window.open("/data-transfer/import", "_blank"),
      },
    ]

    return result
  }, [onCatalogExportClick, onProductsDeleteClick, selectedProductIds, t])

  return (
    <ButtonWithMenu
      loading={isProductsDeleteLoading}
      items={items}
      disabled={isProductsDeleteLoading}
    >
      {t("products:bulkActions.title")}
    </ButtonWithMenu>
  )
}
